import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onUpdated } from 'vue'
import { useStore } from 'vuex'
import { useHead } from '@unhead/vue'
import { eventsClass } from '@/base/class/events.class'
import { MobileAppClass } from '@/base/class/mobileApp.class'
import { ToolsClass } from '@/base/class/tools.class'
import { MatomoClass } from '@/base/class/matomo.class'
import { LANGUAGES_ACTIVE_LIST } from '@/base/const/lang.const'
import {
  CONTRAST_CONTEXT_SUPPORT_LIST,
  PAYWALL_THEME,
  PAYWALL_THEME_DARK_LIST,
  PAYWALL_THEME_LIST,
  THEME_CONTEXT_SUPPORT_LIST,
} from '@/base/const/paywall.const'
import { COOKIE_LANG, COOKIE_LAYOUT } from '@/base/const/cookies.const'
import { CookieClass } from '@/base/class/cookie.class'
import { getLangDirection } from '@/base/utils/lang-direction'

export function useApp() {
  const store = useStore()
  const proxy = getCurrentInstance()?.proxy

  const contextName = proxy?.$VUE_CONTEXT_NAME

  const isPaywallView = computed(() => store.state.payment.paywallView && !store.state.lang.loading)
  // Ciemna gałąź obejmuje `contrast` — patrz `PAYWALL_THEME_DARK_LIST`. Klasa `dark` musi lecieć
  // także w trybie contrast, bo cała ciemna paleta jest zapisana pod nią, a `.contrast` dokłada
  // wyłącznie różnice.
  const isPaywallThemeDark = computed(() => PAYWALL_THEME_DARK_LIST.includes(store.state.payment.paywallTheme))
  const isPaywallThemeContrast = computed(() => store.state.payment.paywallTheme === PAYWALL_THEME.contrast)
  const lang = computed(() => store.getters.getCurrentLanguage)

  useHead(() => ({
    htmlAttrs: {
      lang: lang.value?.toLowerCase() || 'pl',
      dir: getLangDirection(lang.value),
    },
  }))

  function checkMatomoLoaded() {
    console.time('Matomo Loaded')
    if (!MatomoClass.isMatomoLoaded()) {
      setTimeout(checkMatomoLoaded, 100)
      return
    }
    console.timeEnd('Matomo Loaded')
  }

  function setPaywallTheme(theme = PAYWALL_THEME.light) {
    if (PAYWALL_THEME_LIST.includes(theme)) {
      store.commit('SET_PAYWALL_THEME', theme)
      CookieClass.setCookie(COOKIE_LAYOUT, theme, 1, '.autopay.eu')
    }
  }

  function setInitialLanguage(initLang = null) {
    if (initLang && LANGUAGES_ACTIVE_LIST.includes(initLang)) {
      void store.dispatch('setLanguage', { value: initLang.toUpperCase(), silent: true })
      return
    }
    const cookieLang = CookieClass.getCookie(COOKIE_LANG)
    if (cookieLang && LANGUAGES_ACTIVE_LIST.includes(cookieLang.toUpperCase())) {
      void store.dispatch('setLanguage', { value: cookieLang.toUpperCase(), silent: true })
    }
  }

  function setPageTheme(layout) {
    if (!THEME_CONTEXT_SUPPORT_LIST.includes(contextName)) {
      setPaywallTheme(PAYWALL_THEME.light)
      return
    }
    // Contrast wchodzi WYŁĄCZNIE z jawnego `?layout=contrast` — nie ma media query, z której dałoby
    // się go wywnioskować. W kontekście z motywem, ale bez contrastu, degradujemy do `dark`:
    // żądanie było o wariant ciemny, więc jasna bramka byłaby gorszą odpowiedzią niż brak żółtego.
    if (layout === PAYWALL_THEME.contrast) {
      setPaywallTheme(
        CONTRAST_CONTEXT_SUPPORT_LIST.includes(contextName) ? PAYWALL_THEME.contrast : PAYWALL_THEME.dark
      )
      return
    }
    if (layout === PAYWALL_THEME.dark) {
      setPaywallTheme(PAYWALL_THEME.dark)
      return
    }
    if (layout === PAYWALL_THEME.light) {
      setPaywallTheme(PAYWALL_THEME.light)
      return
    }
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setPaywallTheme(PAYWALL_THEME.dark)
      return
    }
    setPaywallTheme(PAYWALL_THEME.light)
  }

  const queryParams = ToolsClass.getQueryParams()
  setPageTheme(queryParams.layout)
  setInitialLanguage(queryParams.lang)
  store.commit('SET_PAYMENT_CONTEXT_NAME', contextName)
  eventsClass.addEventsHandlers()
  if (queryParams.VersionSDK) {
    MobileAppClass.setVersionSDKParam(queryParams.VersionSDK)
  }
  if (ToolsClass.isMobileSDKMockEnabled()) {
    MobileAppClass.initMobileSDKMock()
  }
  MobileAppClass.supportExternalLinks()
  ToolsClass.disableBrowserRefresh()

  onBeforeUnmount(() => {
    eventsClass.removeEventsHandlers()
  })

  onUpdated(() => {
    void nextTick(() => {
      MobileAppClass.supportExternalLinks()
    })
  })

  return {
    contextName,
    isPaywallView,
    isPaywallThemeDark,
    isPaywallThemeContrast,
    lang,
    checkMatomoLoaded,
    setPaywallTheme,
    setInitialLanguage,
    setPageTheme,
  }
}
